import { useContext, useMemo } from 'react'

import { getIsSizeWithinBounds } from '@lib/size'
import { type SanityFrameBuilderStrings } from '@frame-builder/data/sanity/queries/types/strings'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderSelectedStateContext } from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import {
  type FrameBuilderConfigurationWithLimits,
  type FrameBuilderState,
  type ValidationErrorMessages,
} from '@frame-builder/lib/types'
import { getStateFrameSize } from '../size'
import {
  getIsArtworkImageValid,
  getIsArtworkSizeValid,
  getIsGlassValid,
  getIsPassepartoutColorValid,
  getIsPassepartoutSizeValid,
  getIsProfileValid,
  getIsSpacerColorValid,
  getIsSpacerSizeValid,
  getIsTextureValid,
} from './state-validation'

/**
 * Gets artwork size selection state error message.
 */
export const getArtworkSizeSelectionError = (
  frameBuilderConfiguration: FrameBuilderConfigurationWithLimits,
  state: FrameBuilderState,
  frameBuilderStrings: SanityFrameBuilderStrings,
): string | undefined => {
  const artworkWidth = state.artwork.width
  const artworkHeight = state.artwork.height

  if (!artworkWidth && !artworkHeight) {
    return frameBuilderStrings.artworkSizeNoSelection
  }

  if (!artworkWidth) {
    return frameBuilderStrings.artworkSizeNoWidthSelected
  }

  if (!artworkHeight) {
    return frameBuilderStrings.artworkSizeNoHeightSelected
  }

  // Check if width or height is out bounds (global moulding minimum or maximum)
  const isSizeValid = getIsSizeWithinBounds(
    [artworkWidth, artworkHeight],
    [
      frameBuilderConfiguration.limits.minimumSize,
      frameBuilderConfiguration.limits.minimumSize,
    ],
    [
      frameBuilderConfiguration.limits.maximumWidth,
      frameBuilderConfiguration.limits.maximumHeight,
    ],
  )

  if (!isSizeValid) {
    const min = frameBuilderConfiguration.limits.minimumSize
    const max = Math.max(
      frameBuilderConfiguration.limits.maximumWidth,
      frameBuilderConfiguration.limits.maximumHeight,
    )

    return `${frameBuilderStrings.artworkSizeOutOfProfileLimits}`
      .replace('{min}', `${min} ${frameBuilderStrings.lengthUnitLabel}`)
      .replace('{max}', `${max} ${frameBuilderStrings.lengthUnitLabel}`)
      .replace(
        '{maxWidth}',
        `${frameBuilderConfiguration.limits.maximumWidth} ${frameBuilderStrings.lengthUnitLabel}`,
      )
      .replace(
        '{maxHeight}',
        `${frameBuilderConfiguration.limits.maximumHeight} ${frameBuilderStrings.lengthUnitLabel}`,
      )
  }
}

/**
 * Manages step definition validation error messages.
 */
export const useValidationErrorMessages = () => {
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)

  return useMemo(() => {
    const frameSize = getStateFrameSize(selectedState)

    const isArtworkSizeValid = getIsArtworkSizeValid(
      frameBuilderConfiguration,
      selectedState.artwork.width,
      selectedState.artwork.height,
    )
    const artworkSizeSelectionError = getArtworkSizeSelectionError(
      frameBuilderConfiguration,
      selectedState,
      frameBuilderStrings,
    )
    const isArtworkImageValid = getIsArtworkImageValid(
      selectedState.artwork.image,
    )
    const isProfileValid = getIsProfileValid(frameSize, selectedState.profile)
    const isTextureValid = getIsTextureValid(
      frameBuilderConfiguration,
      selectedState.profile,
      selectedState.texture,
    )
    const isPassepartoutColorValid = getIsPassepartoutColorValid(
      frameBuilderType,
      selectedState.profile,
      selectedState.passepartout.color,
    )
    const isPassepartoutSizeValid = getIsPassepartoutSizeValid(
      selectedState.artwork.width,
      selectedState.artwork.height,
      selectedState.passepartout.color,
      selectedState.passepartout.width,
      selectedState.passepartout.height,
    )
    const isGlassValid = getIsGlassValid(selectedState.glass)
    const isSpacerSizeValid = getIsSpacerSizeValid(
      selectedState.profile,
      selectedState.spacerSize,
    )
    const isSpacerColorValid = getIsSpacerColorValid(
      selectedState.spacerSize,
      selectedState.spacerColor,
    )

    const validationErrorMessages: ValidationErrorMessages = {
      artworkSize: artworkSizeSelectionError,
    }

    if (!isArtworkSizeValid) {
      validationErrorMessages.artowkSizePreview =
        frameBuilderStrings.artworkSizeSizeNotValid
    }

    if (!isArtworkImageValid) {
      validationErrorMessages.artworkImage =
        frameBuilderStrings.artworkPreviewNoSelection
    }

    if (!isProfileValid) {
      validationErrorMessages.frameStyleProfilePreview =
        frameBuilderStrings.frameStyleProfileNotValid
    }

    if (!isTextureValid) {
      validationErrorMessages.frameStyleTexturePreview =
        frameBuilderStrings.frameStyleTextureNotValid
    }

    if (!isPassepartoutSizeValid) {
      validationErrorMessages.passepartoutSizePreview =
        frameBuilderStrings.passepartoutSizeNotValid
    }

    if (!isPassepartoutColorValid) {
      validationErrorMessages.passepartoutColorPreview =
        frameBuilderStrings.passepartoutColorNotValid
    }

    if (!isGlassValid) {
      validationErrorMessages.glassTypePreview =
        frameBuilderStrings.glassTypeNotValid
    }

    if (!isSpacerSizeValid) {
      validationErrorMessages.spacerSizePreview =
        frameBuilderStrings.spacerSizeNotValid
    }

    if (!isSpacerColorValid) {
      validationErrorMessages.spacerColorPreview =
        frameBuilderStrings.spacerColorNotValid
    }

    return validationErrorMessages
  }, [
    frameBuilderConfiguration,
    frameBuilderStrings,
    frameBuilderType,
    selectedState,
  ])
}
